import useCommonStyles from '../../../../../../../commonStyles';
import { useContext, useMemo, useState } from 'react';

import { Box, Grid, Button } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Divider } from '@mui/material';
import { usePageLevelScriptConfigContext } from '../../../../../../../Contexts/PageLevelScriptConfigContext';
import { UnitsContext } from '../../../../../../../Contexts/UnitsContext';
import { Modalv2Context } from '../../../../../../../Contexts/Modalv2Context';
import VideoSectionSplitCard from '../../../VideoSectionSplitCard';
import SplitVisualizerIcon from '../../../../../../../Components/PieChartVisualizer';
import DropDownSingle from '../../../../../../../Components/DropDownSingle';
import SwitchInput from '../../../../../../../Components/SwitchInput';
import { SPLIT_CONFIG_TYPES } from '../../../../../../../core/constants';
import {
	checkIfNewSplitCanBeAdded,
	getDataForSplitVisualizer
} from '../../../../../../../utilities/sectionRefHelpers';
import { toast } from 'react-toastify';

const { NORMAL_SPLIT } = SPLIT_CONFIG_TYPES;

/**
 * PageLevelScriptSectionSplitConfig: Manages section split configs for a specific page.
 * - Displays a switch to enable or disable section splits.
 * - Shows a visualizer for section split percentages.
 * - Renders cards for each section to update or delete split percentages.
 * - Provides an option to add new sections to the split config.
 */
const PageLevelScriptSectionSplitConfig = (props) => {
	const commonStyleClasses = useCommonStyles();
	const {
		pageLevelScriptConfig,
		pageLevelScriptConfigPage,
		updateSectionLevelSplitForPageLevelConfig
	} = usePageLevelScriptConfigContext();

	const currentPageConfig = pageLevelScriptConfig[pageLevelScriptConfigPage - 1];
	const currentPageScriptConfig = currentPageConfig['pageLevelScriptConfig'];
	const { updateModalContent } = useContext(Modalv2Context);
	const sectionLevelSplit = currentPageScriptConfig.sectionLevelSplit;
	const { units } = useContext(UnitsContext);

	const sectionSplits = sectionLevelSplit.splits;
	const dataForSplitVisualizer = getDataForSplitVisualizer(units, sectionSplits);

	const deleteSectionFromSplitConfig = (videoSectionId) => {
		const newSplitConfig = structuredClone(sectionLevelSplit);
		delete newSplitConfig.splits[videoSectionId];
		updateSectionLevelSplitForPageLevelConfig(newSplitConfig, currentPageConfig.id);
	};

	/**
	 * @name updateNormalTypeSplitPercentage
	 * @description This function updates the split percentage of a section split
	 * @param {string} sectionId
	 * @param {number} newPercentage
	 * @returns
	 */
	const updateNormalTypeSplitPercentage = (sectionId, newPercentage) => {
		const newSplitConfig = structuredClone(sectionLevelSplit);
		const videoSectionSplit = newSplitConfig.splits[sectionId];
		// Throw error if videoSectionSplit is not found
		if (!videoSectionSplit) {
			return toast.error('Section Id Not Found in Splits!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}

		videoSectionSplit.splitPercentage = newPercentage;
		updateSectionLevelSplitForPageLevelConfig(newSplitConfig, currentPageConfig.id);
	};

	const handleSectionLevelSplitEnabled = (booleanValue) => {
		const newSplitConfig = structuredClone(sectionLevelSplit);
		newSplitConfig.enabled = booleanValue;
		updateSectionLevelSplitForPageLevelConfig(newSplitConfig, currentPageConfig.id);
	};

	return (
		<Box style={{ padding: '3.5rem 0' }}>
			<Grid container spacing={2}>
				<Grid item xs={6} style={{ paddingLeft: '3rem' }}>
					<SwitchInput
						isObjectEditor={false}
						directValue={sectionLevelSplit.enabled}
						updateDirectValue={() => {
							handleSectionLevelSplitEnabled(!sectionLevelSplit.enabled);
						}}
						label="Enabled"
					/>
				</Grid>
				<Grid item xs={6} style={{ width: '10rem' }}>
					<SplitVisualizerIcon
						data={dataForSplitVisualizer}
						sumRes={100}
						updateModalContent={updateModalContent}
					/>
				</Grid>
			</Grid>
			<Divider
				style={{ width: '95%', margin: '0 auto', marginBottom: '3rem', marginTop: '3rem' }}
			/>
			<Grid container spacing={2}>
				{Object.keys(sectionLevelSplit.splits).map((videoSectionId) => {
					return (
						<Grid item xs={6} key={videoSectionId}>
							<VideoSectionSplitCard
								units={units}
								videoSectionId={videoSectionId}
								sectionSplitData={sectionLevelSplit.splits[videoSectionId]}
								updateNormalTypeSplitPercentage={updateNormalTypeSplitPercentage}
								deleteSectionFromSplitConfig={deleteSectionFromSplitConfig}
							/>
						</Grid>
					);
				})}
			</Grid>
			<Grid item xs={12} style={{ display: 'flex' }}>
				{Object.keys(sectionLevelSplit.splits).length < Object.keys(units).length && (
					<AddCircleOutlineOutlinedIcon
						className={commonStyleClasses.icon}
						color="primary"
						fontSize="medium"
						style={{ margin: '0 auto' }}
						onClick={() => {
							updateModalContent(
								<PageLevelScriptAddSectionSplitModal
									updateModalContent={updateModalContent}
									updateSectionLevelSplitForPageLevelConfig={
										updateSectionLevelSplitForPageLevelConfig
									}
									sectionLevelSplit={sectionLevelSplit}
									currentPageConfig={currentPageConfig}
									units={units}
								/>
							);
						}}
					/>
				)}
			</Grid>
		</Box>
	);
};

/**
 * PageLevelScriptAddSectionSplitModal: Modal component to add a new section split.
 * - Lets the user select from available sections not already added to the split.
 */
const PageLevelScriptAddSectionSplitModal = ({
	updateModalContent,
	units,
	sectionLevelSplit,
	currentPageConfig,
	updateSectionLevelSplitForPageLevelConfig
}) => {
	const [selectedExistingUnitSectionRef, setSelectedExistingUnitSectionRef] = useState('');
	const commonStyleClasses = useCommonStyles();
	const availableSections = useMemo(() => {
		const alreadyAddedSections = new Set(
			sectionLevelSplit ? Object.keys(sectionLevelSplit.splits) : []
		);
		const availableSectionRefs = [];

		for (const sectionRef in units) {
			const abSplitTestData = units[sectionRef]?.abSplitTestData;
			const videoSectionId = units[sectionRef].videoSectionId;
			const considerSectionRefCondition =
				!alreadyAddedSections.has(videoSectionId) && !abSplitTestData;

			if (considerSectionRefCondition) {
				availableSectionRefs.push(sectionRef);
			}
		}
		return availableSectionRefs;
	}, [sectionLevelSplit]);

	/**
	 * @name modifySplitConfigToAddNewSplit
	 * This function modifies the section Split config to add a new split.
	 * @returns {object} clonedSectionLevelSplit - The Updated Split Config
	 */
	const modifySplitConfigToAddNewSplit = () => {
		const clonedSectionLevelSplit = structuredClone(sectionLevelSplit);
		const videoSectionId = units[selectedExistingUnitSectionRef].videoSectionId;
		clonedSectionLevelSplit.splits[videoSectionId] = {
			type: NORMAL_SPLIT,
			splitPercentage: 0
		};

		return clonedSectionLevelSplit;
	};

	const addNewSplitSection = () => {
		try {
			checkIfNewSplitCanBeAdded(
				units,
				selectedExistingUnitSectionRef,
				NORMAL_SPLIT, // since, there'll be only NORMAL_TYPE in pglc
				sectionLevelSplit
			); // validation check before adding split
			const newSplitConfig = modifySplitConfigToAddNewSplit();
			updateSectionLevelSplitForPageLevelConfig(newSplitConfig, currentPageConfig.id);
			updateModalContent(null);
		} catch (error) {
			toast.error(error.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	return (
		<Grid container spacing={2} className={commonStyleClasses.modalContentStyle}>
			<Grid item xs={12}>
				<DropDownSingle
					isObjectEditor={false}
					directValue={selectedExistingUnitSectionRef}
					updateDirectValue={(value) => {
						setSelectedExistingUnitSectionRef(value);
					}}
					list={availableSections.map((sectionRef) => {
						const sectionName = units[sectionRef].name;
						return {
							label: sectionName,
							value: sectionRef
						};
					})}
					label="Add New Section Split"
				/>
			</Grid>
			<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
				<Button
					variant="contained"
					style={{ width: '10rem', height: '5rem' }}
					onClick={addNewSplitSection}
				>
					ADD
				</Button>
			</Grid>
		</Grid>
	);
};

export default PageLevelScriptSectionSplitConfig;
